import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, subDays, startOfDay, isBefore } from 'date-fns';
import { 
  Printer, 
  Truck,
  Package,
  MapPin,
  RefreshCw,
  Check,
  LogOut,
  Globe,
  ChevronLeft,
  ChevronRight,
  Camera,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Image
} from 'lucide-react';

const DriverViewPage = () => {
  const { API_URL, getAuthHeader, user, logout } = useAuth();
  const { t, language, setLanguage, languageNames, languageFlags, availableLanguages } = useLanguage();
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Delivery confirmation state
  const [deliveryStatus, setDeliveryStatus] = useState('full');
  const [boxesReturned, setBoxesReturned] = useState(0);
  const [amountCollected, setAmountCollected] = useState(0);
  const [deliveredItems, setDeliveredItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  // Photo upload state
  const [photoBase64, setPhotoBase64] = useState('');
  const [photoNotes, setPhotoNotes] = useState('');
  const fileInputRef = useRef(null);
  
  // WhatsApp notification state
  const [sendWhatsAppNotification, setSendWhatsAppNotification] = useState(true);
  const [notificationMessageType, setNotificationMessageType] = useState('delivery');
  const [sendingNotification, setSendingNotification] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [deliveryRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/driver/delivery-sheet/${dateStr}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() })
      ]);
      const sorted = deliveryRes.data.sort((a, b) => (a.delivery_order || 999) - (b.delivery_order || 999));
      setDeliveryItems(sorted);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching delivery sheet:', error);
      toast.error(t('loadError') || 'خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const productColumns = products.filter(p => productTotals[p.id] > 0);
    
    const productHeaders = productColumns.map(p => 
      `<th style="padding: 6px; border: 1px solid #ccc; background: #f8f8f8; font-size: 10px; color: ${p.color || '#000000'};">${p.name}</th>`
    ).join('');
    
    const rows = deliveryItems.map((item, index) => {
      const productCells = productColumns.map(p => {
        const orderItem = item.items?.find(i => i.product_id === p.id);
        const qty = orderItem?.quantity || 0;
        return `<td style="padding: 6px; border: 1px solid #ccc; text-align: center; font-weight: bold; color: ${p.color || '#000000'};">${qty || '-'}</td>`;
      }).join('');
      
      return `
        <tr>
          <td style="padding: 6px; border: 1px solid #ccc; text-align: center; background: #fef3c7;">${item.delivery_order || index + 1}</td>
          <td style="padding: 6px; border: 1px solid #ccc;">
            <strong>${item.customer_name}</strong>
            ${item.shop_name ? `<br/><small style="color: #666;">${item.shop_name}</small>` : ''}
          </td>
          <td style="padding: 6px; border: 1px solid #ccc; font-size: 10px;">${item.address || '-'}</td>
          ${productCells}
          <td style="padding: 6px; border: 1px solid #ccc; font-size: 10px;">${item.notes || '-'}</td>
          <td style="padding: 6px; border: 1px solid #ccc; width: 50px;"></td>
        </tr>
      `;
    }).join('');
    
    const totalCells = productColumns.map(p => 
      `<td style="padding: 6px; border: 1px solid #ccc; text-align: center; font-weight: bold; background: #d1fae5;">${productTotals[p.id] || 0}</td>`
    ).join('');
    
    const printHTML = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>ورقة التوصيل - السائق</title>
        <style>
          @page { size: A4 landscape; margin: 8mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 10px; direction: rtl; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 10px;">
          <h2 style="margin: 0;">ورقة التوصيل</h2>
          <p style="margin: 5px 0; color: #666;">${format(selectedDate, 'yyyy/MM/dd')}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="padding: 6px; border: 1px solid #ccc; background: #fef3c7; width: 30px;">#</th>
              <th style="padding: 6px; border: 1px solid #ccc; background: #f8f8f8;">العميل</th>
              <th style="padding: 6px; border: 1px solid #ccc; background: #f8f8f8; width: 80px;">العنوان</th>
              ${productHeaders}
              <th style="padding: 6px; border: 1px solid #ccc; background: #f8f8f8; width: 60px;">ملاحظات</th>
              <th style="padding: 6px; border: 1px solid #ccc; background: #f8f8f8; width: 50px;">توقيع</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
            <tr style="background: #d1fae5;">
              <td colspan="3" style="padding: 6px; border: 1px solid #ccc; font-weight: bold; text-align: center;">المجموع</td>
              ${totalCells}
              <td style="padding: 6px; border: 1px solid #ccc;"></td>
              <td style="padding: 6px; border: 1px solid #ccc;"></td>
            </tr>
          </tbody>
        </table>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  const openConfirmDialog = (item) => {
    setSelectedItem(item);
    setDeliveryStatus(item.delivery_status === 'pending' ? 'full' : item.delivery_status);
    setBoxesReturned(item.boxes_returned || 0);
    setAmountCollected(item.amount_collected || 0);
    setDeliveredItems(item.items.map(i => ({
      product_id: i.product_id,
      quantity: i.quantity,
      delivered_quantity: i.delivered_quantity ?? i.quantity
    })));
    setDeliveryNotes('');
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelivery = async () => {
    try {
      const data = {
        delivery_status: deliveryStatus,
        boxes_returned: boxesReturned,
        amount_collected: amountCollected,
        notes: deliveryNotes
      };
      
      if (deliveryStatus === 'partial') {
        data.delivered_items = deliveredItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          delivered_quantity: item.delivered_quantity
        }));
      }
      
      await axios.put(
        `${API_URL}/api/orders/${selectedItem.order_id}/confirm-delivery`,
        data,
        { headers: getAuthHeader() }
      );
      
      toast.success(t('deliveryConfirmed') || 'تم تأكيد التوصيل بنجاح');
      
      // Send WhatsApp notification if enabled
      if (sendWhatsAppNotification) {
        await sendDeliveryNotification(selectedItem.order_id, boxesReturned, notificationMessageType);
      }
      
      setConfirmDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error confirming delivery:', error);
      toast.error(t('saveError') || 'خطأ في حفظ البيانات');
    }
  };
  
  const sendDeliveryNotification = async (orderId, boxes, messageType) => {
    setSendingNotification(true);
    try {
      await axios.post(
        `${API_URL}/api/orders/${orderId}/send-delivery-notification`,
        {
          order_id: orderId,
          boxes_returned: boxes,
          message_type: messageType
        },
        { headers: getAuthHeader() }
      );
      toast.success('تم إرسال إشعار التسليم عبر واتساب');
    } catch (error) {
      console.error('Error sending notification:', error);
      // Don't show error toast - notification is optional
      if (error.response?.data?.detail) {
        toast.info(error.response.data.detail);
      }
    } finally {
      setSendingNotification(false);
    }
  };

  const handleBoxesUpdate = async (orderId, newValue) => {
    try {
      await axios.put(
        `${API_URL}/api/orders/${orderId}/boxes-returned`,
        { boxes_returned: parseInt(newValue) || 0 },
        { headers: getAuthHeader() }
      );
      toast.success(t('saved') || 'تم الحفظ');
      fetchData();
    } catch (error) {
      console.error('Error updating boxes:', error);
      toast.error(t('saveError') || 'خطأ');
    }
  };

  const openPhotoDialog = (item) => {
    setSelectedItem(item);
    setPhotoBase64('');
    setPhotoNotes('');
    setPhotoDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProof = async () => {
    if (!photoBase64) {
      toast.error(t('selectPhoto') || 'الرجاء اختيار صورة');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/orders/${selectedItem.order_id}/proof-of-delivery`,
        {
          order_id: selectedItem.order_id,
          image_base64: photoBase64,
          notes: photoNotes
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(t('proofUploaded') || 'تم رفع إثبات الاستلام بنجاح');
      setPhotoDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error(t('uploadError') || 'خطأ في رفع الصورة');
    }
  };

  const formattedDate = format(selectedDate, 'yyyy/MM/dd');

  const today = startOfDay(new Date());
  const twoDaysAgo = startOfDay(subDays(new Date(), 2));
  const canGoBack = !isBefore(startOfDay(subDays(selectedDate, 1)), twoDaysAgo);
  const canGoForward = isBefore(startOfDay(selectedDate), today);

  const goToPreviousDay = () => {
    if (canGoBack) {
      setSelectedDate(subDays(selectedDate, 1));
    }
  };

  const goToNextDay = () => {
    if (canGoForward) {
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setSelectedDate(nextDay);
    }
  };

  const productMap = products.reduce((acc, p) => {
    acc[p.id] = p;
    return acc;
  }, {});

  const productTotals = {};
  let totalBoxes = 0;
  deliveryItems.forEach(item => {
    item.items?.forEach(orderItem => {
      const productId = orderItem.product_id;
      if (!productTotals[productId]) {
        productTotals[productId] = 0;
      }
      productTotals[productId] += orderItem.quantity || 0;
      totalBoxes += orderItem.quantity || 0;
    });
  });

  const getProductQuantity = (item, productId) => {
    const found = item.items?.find(i => i.product_id === productId);
    return found ? found.quantity : 0;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'full':
        return <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t('delivered') || 'تم التسليم'}</span>;
      case 'partial':
        return <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t('partialDelivery') || 'جزئي'}</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded-full">{t('pending') || 'في الانتظار'}</span>;
    }
  };

  const LanguageSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Globe className="w-4 h-4" />
          <span>{languageFlags[language]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {availableLanguages.map((lang) => (
          <DropdownMenuItem 
            key={lang} 
            onClick={() => setLanguage(lang)}
            className={language === lang ? 'bg-amber-50' : ''}
          >
            <span className="me-2">{languageFlags[lang]}</span>
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-amber-600 text-white p-4 no-print">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">{t('driverView') || 'واجهة السائق'}</h1>
              <p className="text-amber-100 text-sm">{t('driverWelcome') || 'مرحباً بك'}, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" onClick={logout} className="text-white hover:bg-amber-700">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4">
        {/* Print-Only Header */}
        <div className="hidden print:block mb-6" style={{fontFamily: 'Arial, Tahoma, sans-serif'}}>
          <div style={{textAlign: 'center', borderBottom: '3px solid #d97706', paddingBottom: '10px', marginBottom: '15px'}}>
            <h1 style={{fontSize: '22px', fontWeight: 'bold', margin: 0}}>ورقة التوصيل</h1>
            <p style={{fontSize: '14px', color: '#555', margin: '5px 0'}}>{user?.name}</p>
            <p style={{fontSize: '16px', fontWeight: 'bold'}}>{formattedDate}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="no-print flex flex-wrap gap-3 mb-6 items-center justify-between bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-stone-100 rounded-lg p-1">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={goToPreviousDay}
                disabled={!canGoBack}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="font-medium px-3 py-1 min-w-[120px] text-center">
                {formattedDate}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={goToNextDay}
                disabled={!canGoForward}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 me-2" />
              {t('refreshList') || 'تحديث القائمة'}
            </Button>
          </div>
          <Button onClick={handlePrint} className="bg-amber-600 hover:bg-amber-700">
            <Printer className="w-4 h-4 me-2" />
            {t('print') || 'طباعة'}
          </Button>
        </div>

        {/* Product Totals */}
        {deliveryItems.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50 print:bg-yellow-50 print:border print:border-gray-400">
            <CardContent className="p-4">
              <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('totalBoxes') || 'إجمالي الصناديق'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {products.map((product) => {
                  const qty = productTotals[product.id] || 0;
                  if (qty === 0) return null;
                  return (
                    <div key={product.id} className="bg-white rounded-lg p-3 text-center border border-amber-200">
                      <p className="text-2xl font-bold text-amber-700">{qty}</p>
                      <p className="text-sm text-stone-600">{product.name}</p>
                    </div>
                  );
                })}
                <div className="bg-emerald-100 rounded-lg p-3 text-center border border-emerald-300">
                  <p className="text-2xl font-bold text-emerald-700">{totalBoxes}</p>
                  <p className="text-sm text-stone-600">{t('total') || 'الإجمالي'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deliveries */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
          </div>
        ) : deliveryItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Truck className="w-16 h-16 mx-auto text-stone-300 mb-4" />
            <h3 className="text-xl font-bold text-stone-700">{t('noDeliveries') || 'لا توجد توصيلات'}</h3>
            <p className="text-stone-500">{t('noOrdersForDate') || 'لا توجد طلبات لهذا التاريخ'}</p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden no-print space-y-4">
              {deliveryItems.map((item, index) => (
                <Card key={item.order_id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-bold">
                          {item.delivery_order || index + 1}
                        </span>
                        <div>
                          <h4 className="font-bold text-stone-900">{item.customer_name}</h4>
                          {item.shop_name && <p className="text-sm text-stone-500">{item.shop_name}</p>}
                        </div>
                      </div>
                      {getStatusBadge(item.delivery_status)}
                    </div>
                    
                    {/* Address and GPS Location */}
                    <div className="mb-3 space-y-1">
                      {item.address && (
                        <div className="text-sm text-stone-600 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{item.address}</span>
                        </div>
                      )}
                      {(item.latitude && item.longitude) && (
                        <a 
                          href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                        >
                          🗺️ فتح الخريطة للتوصيل
                        </a>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {item.items?.map((orderItem, idx) => {
                        const product = products.find(p => p.id === orderItem.product_id);
                        return (
                          <div 
                            key={idx} 
                            className="bg-stone-100 px-3 py-2 rounded-lg flex flex-col items-center gap-1"
                            style={{ color: product?.color || '#000000' }}
                          >
                            {product?.image_base64 && (
                              <img 
                                src={product.image_base64} 
                                alt={orderItem.product_name}
                                className="w-10 h-10 object-contain rounded"
                              />
                            )}
                            <span className="text-xs">{orderItem.product_name}</span>
                            <span className="font-bold text-lg">{orderItem.quantity}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Amount to collect */}
                    {item.amount_to_collect > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3">
                        <p className="text-sm text-blue-600 flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {t('amountToCollect') || 'مبلغ للاستلام'}
                        </p>
                        <p className="text-xl font-bold text-blue-700">€{item.amount_to_collect.toFixed(2)}</p>
                        {item.amount_collected > 0 && (
                          <p className="text-sm text-emerald-600">{t('collected') || 'تم استلام'}: €{item.amount_collected.toFixed(2)}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Boxes Return */}
                    <div className="flex items-center justify-between pt-3 border-t mb-3">
                      <div>
                        <p className="text-sm text-stone-500">{t('boxesToReturn') || 'صناديق للإرجاع'}</p>
                        <p className="font-bold text-lg text-red-600">{item.boxes_to_return}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">{t('boxesReturned') || 'صناديق مرجعة'}</p>
                        <p className="font-bold text-lg text-emerald-600">{item.boxes_returned}</p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        onClick={() => openConfirmDialog(item)}
                      >
                        <Check className="w-4 h-4 me-1" />
                        {t('confirmDelivery') || 'تأكيد التوصيل'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => openPhotoDialog(item)}
                        className={item.proof_uploaded ? 'border-emerald-500 text-emerald-600' : ''}
                      >
                        <Camera className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block print:block">
              <Card className="border-stone-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50">
                      <TableHead className="text-center font-bold w-12">#</TableHead>
                      <TableHead className="text-right font-bold">{t('customerName') || 'اسم الزبون'}</TableHead>
                      {products.map((product) => (
                        <TableHead 
                          key={product.id} 
                          className="text-center font-bold p-2"
                          style={{ color: product.color || '#000000' }}
                        >
                          <div className="flex flex-col items-center gap-1">
                            {product.image_base64 && (
                              <img 
                                src={product.image_base64} 
                                alt={product.name}
                                className="w-10 h-10 object-contain rounded"
                              />
                            )}
                            <span className="text-xs">{product.name}</span>
                          </div>
                        </TableHead>
                      ))}
                      <TableHead className="text-center font-bold text-blue-600">{t('amountToCollect') || 'للاستلام'}</TableHead>
                      <TableHead className="text-center font-bold text-red-600">{t('boxesToReturn') || 'صناديق للإرجاع'}</TableHead>
                      <TableHead className="text-center font-bold no-print">{t('status') || 'الحالة'}</TableHead>
                      <TableHead className="text-center font-bold no-print">{t('actions') || 'إجراءات'}</TableHead>
                      <TableHead className="text-right font-bold">{t('address') || 'العنوان'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveryItems.map((item, index) => (
                      <TableRow key={item.order_id} className="hover:bg-stone-50/50">
                        <TableCell className="text-center font-bold">{item.delivery_order || index + 1}</TableCell>
                        <TableCell>
                          <p className="font-medium">{item.customer_name}</p>
                          {item.shop_name && <p className="text-xs text-stone-500">{item.shop_name}</p>}
                        </TableCell>
                        {products.map((product) => {
                          const qty = getProductQuantity(item, product.id);
                          return (
                            <TableCell 
                              key={product.id} 
                              className="text-center font-bold p-2"
                              style={{ color: product.color || '#000000' }}
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-lg">{qty > 0 ? qty : '-'}</span>
                                {qty > 0 && product.image_base64 && (
                                  <img 
                                    src={product.image_base64} 
                                    alt={product.name}
                                    className="w-8 h-8 object-contain rounded"
                                  />
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center font-bold text-blue-600">
                          {item.amount_to_collect > 0 ? `€${item.amount_to_collect.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="text-center font-bold text-red-600">
                          <input
                            type="number"
                            min="0"
                            defaultValue={item.boxes_to_return || 0}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              if (val !== (item.boxes_to_return || 0)) {
                                handleBoxesUpdate(item.order_id, val);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.target.blur();
                            }}
                            className="w-14 text-center font-bold text-red-600 border border-red-200 rounded-md p-1 focus:border-red-500 focus:outline-none"
                            data-testid={`boxes-input-${item.order_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center no-print">{getStatusBadge(item.delivery_status)}</TableCell>
                        <TableCell className="text-center no-print">
                          <div className="flex gap-1 justify-center">
                            <Button 
                              size="sm" 
                              className="bg-amber-600 hover:bg-amber-700"
                              onClick={() => openConfirmDialog(item)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openPhotoDialog(item)}
                              className={item.proof_uploaded ? 'border-emerald-500 text-emerald-600' : ''}
                            >
                              <Camera className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] text-sm text-right">
                          <div className="space-y-1">
                            {item.address && <span>{item.address}</span>}
                            {(item.latitude && item.longitude) && (
                              <a 
                                href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 text-center no-print"
                              >
                                🗺️ الخريطة
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-amber-100 font-bold">
                      <TableCell colSpan={2} className="text-right text-lg">{t('total') || 'الإجمالي'}</TableCell>
                      {products.map((product) => (
                        <TableCell key={product.id} className="text-center text-lg">
                          {productTotals[product.id] || 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-center text-lg text-blue-600">
                        €{deliveryItems.reduce((sum, item) => sum + (item.amount_to_collect || 0), 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center text-lg text-red-600">
                        {deliveryItems.reduce((sum, item) => sum + (item.boxes_to_return || 0), 0)}
                      </TableCell>
                      <TableCell colSpan={2} className="no-print"></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Confirm Delivery Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('confirmDelivery') || 'تأكيد التوصيل'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="font-medium text-lg">{selectedItem?.customer_name}</p>
            
            {/* Delivery Status */}
            <div className="space-y-2">
              <Label>{t('deliveryStatus') || 'حالة التسليم'}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={deliveryStatus === 'full' ? 'default' : 'outline'}
                  className={deliveryStatus === 'full' ? 'bg-emerald-600' : ''}
                  onClick={() => setDeliveryStatus('full')}
                >
                  {t('fullDelivery') || 'تسليم كامل'}
                </Button>
                <Button
                  type="button"
                  variant={deliveryStatus === 'partial' ? 'default' : 'outline'}
                  className={deliveryStatus === 'partial' ? 'bg-amber-600' : ''}
                  onClick={() => setDeliveryStatus('partial')}
                >
                  {t('partialDelivery') || 'تسليم جزئي'}
                </Button>
              </div>
            </div>
            
            {/* Partial Delivery Items */}
            {deliveryStatus === 'partial' && (
              <div className="space-y-2 bg-amber-50 p-3 rounded-lg">
                <Label>{t('deliveredQuantities') || 'الكميات المسلمة'}</Label>
                {deliveredItems.map((item, idx) => {
                  const product = selectedItem?.items?.find(i => i.product_id === item.product_id);
                  return (
                    <div key={item.product_id} className="flex items-center gap-2">
                      <span className="flex-1 text-sm">{product?.product_name}</span>
                      <span className="text-sm text-stone-500">/{item.quantity}</span>
                      <Input
                        type="number"
                        min="0"
                        max={item.quantity}
                        value={item.delivered_quantity}
                        onChange={(e) => {
                          const newItems = [...deliveredItems];
                          newItems[idx].delivered_quantity = parseInt(e.target.value) || 0;
                          setDeliveredItems(newItems);
                        }}
                        className="w-20 text-center"
                      />
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Boxes Returned */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>{t('boxesReturned') || 'الصناديق المرجعة'}</Label>
                <span className="text-sm text-red-600">{t('expected') || 'المتوقع'}: {selectedItem?.boxes_to_return || 0}</span>
              </div>
              <Input
                type="number"
                min="0"
                value={boxesReturned}
                onChange={(e) => setBoxesReturned(parseInt(e.target.value) || 0)}
                className="text-center text-xl font-bold"
              />
            </div>
            
            {/* Amount Collected */}
            {selectedItem?.amount_to_collect > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>{t('amountCollected') || 'المبلغ المستلم'}</Label>
                  <span className="text-sm text-blue-600">{t('toCollect') || 'للاستلام'}: €{selectedItem?.amount_to_collect?.toFixed(2)}</span>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountCollected}
                  onChange={(e) => setAmountCollected(parseFloat(e.target.value) || 0)}
                  className="text-center text-xl font-bold"
                />
              </div>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes') || 'ملاحظات'}</Label>
              <Textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder={t('optionalNotes') || 'ملاحظات اختيارية...'}
                rows={2}
              />
            </div>
            
            {/* WhatsApp Notification Options */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-green-700">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  إرسال إشعار واتساب
                </Label>
                <input
                  type="checkbox"
                  checked={sendWhatsAppNotification}
                  onChange={(e) => setSendWhatsAppNotification(e.target.checked)}
                  className="w-5 h-5 accent-green-600"
                />
              </div>
              
              {sendWhatsAppNotification && (
                <div className="space-y-2 bg-green-50 p-3 rounded-lg">
                  <Label className="text-sm text-green-800">نوع الرسالة</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={notificationMessageType === 'delivery' ? 'default' : 'outline'}
                      className={notificationMessageType === 'delivery' ? 'bg-green-600' : ''}
                      onClick={() => setNotificationMessageType('delivery')}
                    >
                      📦 تأكيد التسليم
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={notificationMessageType === 'accounting' ? 'default' : 'outline'}
                      className={notificationMessageType === 'accounting' ? 'bg-green-600' : ''}
                      onClick={() => setNotificationMessageType('accounting')}
                    >
                      📊 محاسبة
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={notificationMessageType === 'statement' ? 'default' : 'outline'}
                      className={notificationMessageType === 'statement' ? 'bg-green-600' : ''}
                      onClick={() => setNotificationMessageType('statement')}
                    >
                      📄 كشف حساب
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={notificationMessageType === 'order' ? 'default' : 'outline'}
                      className={notificationMessageType === 'order' ? 'bg-green-600' : ''}
                      onClick={() => setNotificationMessageType('order')}
                    >
                      🧾 تفاصيل الطلب
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              {t('cancel') || 'إلغاء'}
            </Button>
            <Button 
              onClick={handleConfirmDelivery} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={sendingNotification}
            >
              {sendingNotification ? (
                <RefreshCw className="w-4 h-4 me-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 me-2" />
              )}
              {t('confirm') || 'تأكيد'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('proofOfDelivery') || 'إثبات الاستلام'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="font-medium">{selectedItem?.customer_name}</p>
            
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>{t('uploadPhoto') || 'رفع صورة'}</Label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              
              {photoBase64 ? (
                <div className="relative">
                  <img src={photoBase64} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 left-2"
                    onClick={() => setPhotoBase64('')}
                  >
                    {t('remove') || 'حذف'}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Camera className="w-8 h-8 text-stone-400" />
                    <span>{t('clickToUpload') || 'اضغط لرفع صورة'}</span>
                  </div>
                </Button>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes') || 'ملاحظات'}</Label>
              <Textarea
                value={photoNotes}
                onChange={(e) => setPhotoNotes(e.target.value)}
                placeholder={t('optionalNotes') || 'ملاحظات اختيارية...'}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPhotoDialogOpen(false)}>
              {t('cancel') || 'إلغاء'}
            </Button>
            <Button onClick={handleUploadProof} className="bg-emerald-600 hover:bg-emerald-700" disabled={!photoBase64}>
              <Image className="w-4 h-4 me-2" />
              {t('uploadProof') || 'رفع الإثبات'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            font-size: 11px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page { 
            size: A4 landscape; 
            margin: 0.8cm;
          }
          .no-print { display: none !important; }
          .min-h-screen { min-height: auto !important; }
          .bg-stone-100 { background: white !important; }
          table { font-size: 11px; }
          td, th { padding: 4px 6px !important; }
        }
      `}</style>
    </div>
  );
};

export default DriverViewPage;
